import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateTimeText, UnmatchedQcResponse } from './api'
import styles from './GodMode.module.css'
import dash from './DashboardPage.module.css'

// The QC scans at one client this week that no item took (API
// services/god-dashboard.js readUnmatchedQc), each with the reason the engine
// stored when it decided (services/qc-association.js), and the nearest receipt
// of the item, so a window too short for how the orchard works shows as gaps
// just past it. Attaching one by hand is done in the client's own Product
// Activity, on the receipt.

const OUTCOME: Record<string, string> = {
  no_receipt: 'No receipt in the window',
  ambiguous: 'Several receipts in the window',
  several_on_cycle: 'Several scans on one receipt',
  late: 'Day already closed',
  held_for_review: 'Held for review',
}

function gap(min: number | null) {
  if (min === null) return 'None within 2 days'
  const a = Math.abs(min)
  const text = a < 60 ? `${a} min` : `${Math.floor(a / 60)} h${a % 60 ? ` ${a % 60} min` : ''}`
  if (a === 0) return 'Same minute'
  return `${text} ${min < 0 ? 'before' : 'after'} the scan`
}

export function QcUnmatchedPage() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const [data, setData] = useState<UnmatchedQcResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await apiJson<UnmatchedQcResponse>(user, `/api/god-mode/clients/${id}/qc-unmatched`))
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [user, id])

  useEffect(() => {
    load()
  }, [load])

  const scans = data?.scans || []
  const byOutcome = Object.entries(
    scans.reduce<Record<string, number>>((m, s) => {
      const k = (s.outcome && OUTCOME[s.outcome]) || s.outcome || 'Unknown'
      return { ...m, [k]: (m[k] || 0) + 1 }
    }, {})
  ).sort((a, b) => b[1] - a[1])
  const rules = [...new Set(scans.map((s) => s.rule).filter(Boolean))]

  return (
    <>
      <PageHeader title={data ? `QC not applied · ${data.client.name}` : 'QC not applied'} />
      <PageBody>
        <div className={styles.stack}>
          <div>
            <Link to="/god-mode" className={styles.secondary}>
              ← Back to dashboard
            </Link>{' '}
            <button type="button" className={styles.secondary} onClick={load}>
              Refresh
            </button>
          </div>
          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <>
              <p className={styles.lead}>
                {scans.length
                  ? `${scans.length} QC scan${scans.length === 1 ? '' : 's'} in the last ${data.days} days not applied to any item`
                  : `Every QC scan in the last ${data.days} days was applied or attached by hand`}
                {byOutcome.length > 0 && `: ${byOutcome.map(([why, n]) => `${n} ${why.toLowerCase()}`).join(', ')}`}.
                {rules.length > 0 && ` Rule: ${rules.join('; ')}.`}
              </p>
              {scans.length > 0 && (
                <div className={styles.tableCard}>
                  <table className={styles.table} aria-label="QC scans not applied">
                    <thead>
                      <tr>
                        <th>Scanned</th>
                        <th>Item</th>
                        <th>Quality</th>
                        <th>Outcome</th>
                        <th title="The receipt of the same item closest in time to the scan">Nearest receipt</th>
                        <th>Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scans.map((s) => (
                        <tr key={s.id}>
                          <td>{dateTimeText(s.at)}</td>
                          <td>{s.item_code ? <code>{s.item_code}</code> : <span className={dash.none}>None</span>}</td>
                          <td>
                            {s.quality_name || <span className={dash.none}>—</span>}
                            {s.qc_factor !== null && <div className={dash.sub}>{Math.round(s.qc_factor * 100)} % credited</div>}
                          </td>
                          <td>{(s.outcome && OUTCOME[s.outcome]) || s.outcome || '—'}</td>
                          <td>{gap(s.nearest_min)}</td>
                          <td>{s.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
